import { memo, useEffect, useMemo, useRef } from 'react';

import { Fullscreen, VFlex, VStack, useFlag } from '@/package';
import {
  type __CES2026__CameraNormalizedConfig,
  useFrameImageViewer,
  useLabelingBoxContext,
} from '@/provider';
import { useTaskFrameStore } from '@/store';

import { bufferToBitmap, drawImageOnCanvasFromBitmap } from '../util';

type ToolImagePopupImageItemProps = {
  index: number;
  onFullScreenClick: (index: number) => void;
};

export const ToolImagePopupImageItem = memo(
  ({ index, onFullScreenClick }: ToolImagePopupImageItemProps) => {
    const { cameraCalibrationConfigMap, currentFrame, selectedTask } =
      useTaskFrameStore();
    const { selectedLabelingBoxList, selectBox } = useLabelingBoxContext();
    const { getImage, __CES2026__generateLabelingBoxListRenderer } =
      useFrameImageViewer();
    const {
      value: isLoading,
      setFalse: loaded,
      setTrue: loading,
    } = useFlag(true);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const imagePath = useMemo(
      () => currentFrame?.imagePath?.[index],
      [index, currentFrame?.imagePath],
    );
    const cameraCalibrationConfig =
      useMemo((): Nullish<__CES2026__CameraNormalizedConfig> => {
        if (!imagePath) return;

        const imageFileName = imagePath.split('/').pop() ?? '';

        const cameraCalibrationConfig =
          cameraCalibrationConfigMap[
            imageFileName.replace(/(\.)([a-z]+)$/i, '')
          ] ?? Object.values(cameraCalibrationConfigMap)[0];

        return cameraCalibrationConfig;
      }, [cameraCalibrationConfigMap, imagePath, index]);

    useEffect(() => {
      const loadImage = async () => {
        if (!canvasRef.current || !imagePath) return;

        loading();

        try {
          const { buffer, height, width } = await getImage(imagePath);
          const bitmap = await bufferToBitmap(buffer, { height, width });

          drawImageOnCanvasFromBitmap(canvasRef.current, bitmap);

          bitmap.close();
        } catch (err) {
          console.error(err);
        }

        loaded();
      };

      loadImage();
    }, [imagePath]);

    useEffect(() => {
      if (
        !cameraCalibrationConfig ||
        !currentFrame ||
        !selectedTask ||
        !canvasRef.current ||
        isLoading
      )
        return;

      const { cleanup } = __CES2026__generateLabelingBoxListRenderer({
        cameraCalibrationConfig,
        labelingBoxList: currentFrame.workData,
        selectedLabelingBoxIdList: selectedLabelingBoxList.map(
          // b => b.id.value!,
          b => b.id,
        ),
        selectedTask,
      }).render(canvasRef.current, {
        boxClick(boxID) {
          selectBox(boxID, true);
        },
      });

      return () => cleanup();
    }, [
      cameraCalibrationConfig,
      currentFrame?.workData,
      isLoading,
      selectedLabelingBoxList,
      selectedTask,
    ]);

    return (
      <VStack
        gap={0.25}
        className="w-full rounded-md border border-gray-200 p-1"
      >
        <VFlex className="w-full" justify="center">
          <div className="flex-1 text-body-md">
            {imagePath?.split('/').at(-1)?.split('.').at(0) ?? `Image ${index}`}
          </div>

          <Fullscreen
            className="size-4 text-gray-400"
            role="button"
            onClick={() => onFullScreenClick(index)}
          />
        </VFlex>

        <VFlex
          className="h-[188px] w-[298px] overflow-hidden rounded-md bg-black"
          items="center"
          justify="center"
        >
          <canvas
            ref={canvasRef}
            className="h-auto max-h-full w-auto max-w-full"
          />
        </VFlex>
      </VStack>
    );
  },
);
